import { and, eq } from "drizzle-orm";
import {
  order,
  order_line,
  order_line_modifier,
  payment,
  type PowerSyncDrizzleDB,
} from "@comandero/powersync";
import { liveOrderLineFilters, livePaymentFilters } from "../live/filters";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";
import {
  type CalcLine,
  changeDue,
  computeOrderTotals,
  isSplitSettled,
  lineTotal,
  lineUnitEffectivePrice,
  remainingDue,
} from "./calc";

/** Tenant-isolation scope for `order` writes. */
const orgScope = (organizationId: string) => [
  { column: order.organization_id, value: organizationId },
];

/**
 * Every `UPDATE … WHERE id = ?` in this file goes through `patchRowVerified`: a
 * raw UPDATE against the PowerSync view can match ZERO rows and return
 * «success» while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). This is the most expensive domain to lose in a
 * POS — orders, order lines, table checks. The mutations **may
 * throw**: the caller must communicate it, never swallow it.
 *
 * `recomputeOrderTotals`/`applyOrderDiscountMutation` filter by
 * `organization_id` through `options.scope` (not a raw `and()`): it is not just for
 * correcting `id` as PK. `infra/sync_streams.yaml` syncs `"order"` with
 * `WHERE organization_id IN my_org_ids` — ALL organizations the user is a `member` of,
 * not just the active one. An employee of two locations has BOTH synced at once, and
 * `orderId` travels in the URL (`?order=`, addressable ticket): without the scope, an
 * `UPDATE` launched from the context of organization A would still land on a row from
 * organization B if the `id` matches. See the security note in `../powersync/verified-write.ts`.
 */

export type OrderType = "dine_in" | "takeaway" | "delivery";
export type PaymentMethod = "cash" | "card" | "transfer" | "other";

export interface CreateOrderOpts {
  type?: OrderType;
  tableId?: string | null;
  waiterUserId?: string | null;
  employeeId?: string | null;
  registerSessionId?: string | null;
  guests?: number;
}

/** Creates an empty (open) order. Returns the id. */
export async function createOrderMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  branchId: string,
  opts: CreateOrderOpts = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(order).values({
    id,
    organization_id: organizationId,
    branch_id: branchId,
    type: opts.type ?? "dine_in",
    table_id: opts.tableId ?? null,
    waiter_user_id: opts.waiterUserId ?? null,
    employee_id: opts.employeeId ?? null,
    register_session_id: opts.registerSessionId ?? null,
    status: "open",
    guests: opts.guests ?? 1,
    subtotal: 0,
    discount: 0,
    tax_total: 0,
    total: 0,
    opened_at: now,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export interface AddLineOpts {
  qty?: number;
  note?: string;
  taxRate?: number;
  modifiers?: { optionId?: string | null; name: string; priceDelta: number }[];
}

/**
 * Adds a line (with its modifiers) to the order and recalculates the totals.
 * CC-7: all writes use `await` before returning.
 */
export async function addOrderLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  product: { id?: string | null; name: string; unitPrice: number },
  opts: AddLineOpts = {},
  options?: VerifiedWriteOptions,
): Promise<string> {
  const lineId = generateId();
  const now = nowISO();
  const qty = Math.max(1, Math.trunc(opts.qty ?? 1));
  const mods = opts.modifiers ?? [];

  const calcLine: CalcLine = {
    unitPrice: product.unitPrice,
    qty,
    taxRate: opts.taxRate ?? 0,
    modifiers: mods.map((m) => ({ priceDelta: m.priceDelta })),
  };
  const unitEffective = lineUnitEffectivePrice(calcLine);
  const total = lineTotal(calcLine);

  await db.insert(order_line).values({
    id: lineId,
    organization_id: organizationId,
    order_id: orderId,
    product_id: product.id ?? null,
    name: product.name,
    qty,
    unit_price: unitEffective,
    line_total: total,
    note: opts.note ?? null,
    kds_status: "pending",
    sort_order: 0,
    created_at: now,
    updated_at: now,
  });

  for (const m of mods) {
    await db.insert(order_line_modifier).values({
      id: generateId(),
      organization_id: organizationId,
      order_line_id: lineId,
      modifier_option_id: m.optionId ?? null,
      name: m.name,
      price_delta: Math.trunc(m.priceDelta),
      created_at: now,
      updated_at: now,
    });
  }

  await recomputeOrderTotals(db, organizationId, orderId, undefined, options);
  return lineId;
}

/**
 * Removes a line and recalculates totals. ANTIFRAUD (ACTIVITY-CORE §1b): the
 * line does NOT disappear — it is a soft-delete recorded in history
 * with the actor (and a reason if it is a post-order void, see
 * `voidOrderLineMutation`). Modifiers remain attached to the
 * voided line so the trail is complete.
 */
export async function removeOrderLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  lineId: string,
  opts?: { reason?: string },
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    order_line,
    order_line.id,
    lineId,
    { deleted_at: nowISO(), void_reason: opts?.reason?.trim() || null },
    options,
  );
  await recomputeOrderTotals(db, organizationId, orderId, undefined, options);
}

/**
 * Voids an ALREADY ORDERED line (an explicit business operation, with an
 * optional reason). Same mechanism as removing it (recorded soft-delete), but
 * the intent is explicit in the call-site name and the reason travels to
 * history ("voided line X — reason: dropped plate").
 */
export async function voidOrderLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  lineId: string,
  reason?: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await removeOrderLineMutation(db, organizationId, orderId, lineId, { reason }, options);
}

/**
 * Restores a voided line from a STILL OPEN order and recalculates totals
 * (the inverse of a line void; paid orders have no way back).
 */
export async function restoreOrderLineMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  lineId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    order_line,
    order_line.id,
    lineId,
    { deleted_at: null, void_reason: null },
    options,
  );
  await recomputeOrderTotals(db, organizationId, orderId, undefined, options);
}

/**
 * Recalculates an order's subtotal/tax/total from its lines.
 * The calculation lives in `calc.ts` (pure). Here we only persist the result.
 */
export async function recomputeOrderTotals(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  discount?: number,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const lines = await db
    .select()
    .from(order_line)
    .where(and(eq(order_line.order_id, orderId), ...liveOrderLineFilters()));

  // We build the CalcLine values. tax_rate is not persisted per line in F1, so
  // we recover the effective amount (unit_price already includes modifiers).
  const calcLines: CalcLine[] = lines.map((l) => ({
    unitPrice: l.unit_price ?? 0,
    qty: l.qty ?? 1,
    taxRate: 0,
  }));

  const current = await db.select().from(order).where(eq(order.id, orderId)).limit(1);
  const existingDiscount = current[0]?.discount ?? 0;
  const totals = computeOrderTotals(calcLines, discount ?? existingDiscount);

  await patchRowVerified(
    db,
    order,
    order.id,
    orderId,
    {
      subtotal: totals.subtotal,
      discount: totals.discount,
      tax_total: totals.taxTotal,
      total: totals.total,
      // CC-12: resend branch_id in every update of `order`.
      branch_id: current[0]?.branch_id ?? null,
    },
    { ...options, scope: orgScope(organizationId) },
  );
}

export async function setOrderStatusMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  status: "open" | "sent" | "paid" | "void",
  options?: VerifiedWriteOptions,
): Promise<void> {
  // CC-12: in EVERY order update we resend `branch_id` to avoid
  // write-path rejection due to a sync race (a PATCH that omits branch_id
  // can collide with a concurrent upsert that does not yet have the row).
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  const patch: Record<string, unknown> = {
    status,
    branch_id: current[0]?.branch_id ?? null,
  };
  if (status === "paid" || status === "void") {
    patch.closed_at = nowISO();
  }
  await patchRowVerified(db, order, order.id, orderId, patch, options);
}

/**
 * Voids an order (void) with an optional reason. The transition and reason are
 * recorded in history with the actor (antifraud): "voided order — reason".
 */
export async function voidOrderMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  reason?: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  await patchRowVerified(
    db,
    order,
    order.id,
    orderId,
    {
      status: "void",
      void_reason: reason?.trim() || null,
      closed_at: nowISO(),
      branch_id: current[0]?.branch_id ?? null,
    },
    options,
  );
}

/**
 * Reopens a closed order (paid/void → open). In a POS this is NOT an "undo":
 * it is an explicit business operation recorded in history with the actor
 * ("reopened order") — the shift cash-up lists it as a reopening.
 */
export async function reopenOrderMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  await patchRowVerified(
    db,
    order,
    order.id,
    orderId,
    {
      status: "open",
      closed_at: null,
      void_reason: null,
      branch_id: current[0]?.branch_id ?? null,
    },
    options,
  );
}

/**
 * Applies (or removes, with 0) an order discount with an optional reason. The
 * `discount` + `discount_reason` change is recorded in history
 * with the actor ("applied discount of X — reason: frequent customer").
 */
export async function applyOrderDiscountMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  discount: number,
  reason?: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const value = Math.max(0, Math.trunc(discount));
  await patchRowVerified(
    db,
    order,
    order.id,
    orderId,
    { discount_reason: value > 0 ? reason?.trim() || null : null },
    { ...options, scope: orgScope(organizationId) },
  );
  await recomputeOrderTotals(db, organizationId, orderId, value, options);
}

/** Assigns an order to an employee (waiter/cashier). CC-12: resends branch_id. */
export async function setOrderEmployeeMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  employeeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  await patchRowVerified(
    db,
    order,
    order.id,
    orderId,
    { employee_id: employeeId, branch_id: current[0]?.branch_id ?? null },
    options,
  );
}

export async function setLineKdsStatusMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
  kdsStatus: "pending" | "cooking" | "ready" | "served",
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, order_line, order_line.id, lineId, { kds_status: kdsStatus }, options);
}

export interface PayOrderResult {
  paymentId: string;
  change: number;
}

/**
 * Records a payment (cash by default) and, if covering total, marks order
 * as paid. Returns change to deliver. Change calculation is pure
 * (calc.ts). CC-7: escrituras con await.
 */
export async function payOrderCashMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  tendered: number,
  opts?: { method?: PaymentMethod; registerSessionId?: string | null; employeeId?: string | null },
  options?: VerifiedWriteOptions,
): Promise<PayOrderResult> {
  const current = await db.select().from(order).where(eq(order.id, orderId)).limit(1);
  const total = current[0]?.total ?? 0;
  const method = opts?.method ?? "cash";
  const change = method === "cash" ? changeDue(tendered, total) : 0;
  const amount = method === "cash" ? Math.min(Math.trunc(tendered), total) : Math.trunc(tendered);

  // Sale attribution: the employee charging it (for the waiter report).
  const employeeId = opts?.employeeId ?? current[0]?.employee_id ?? null;

  const paymentId = generateId();
  const now = nowISO();
  await db.insert(payment).values({
    id: paymentId,
    organization_id: organizationId,
    order_id: orderId,
    register_session_id: opts?.registerSessionId ?? current[0]?.register_session_id ?? null,
    method,
    amount,
    tendered: Math.trunc(tendered),
    change_given: change,
    employee_id: employeeId,
    paid_at: now,
    created_at: now,
    updated_at: now,
  });

  if (employeeId && employeeId !== current[0]?.employee_id) {
    await setOrderEmployeeMutation(db, orderId, employeeId, options);
  }
  await setOrderStatusMutation(db, orderId, "paid", options);
  return { paymentId, change };
}

/**
 * Sends the order to the kitchen: marks the order as `sent` (lines are born
 * with `kds_status = "pending"`). From here the order appears in the KDS.
 * CC-7: write with await.
 */
export async function sendOrderToKitchenMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await setOrderStatusMutation(db, orderId, "sent", options);
}

/** Advances a line's KDS state from pending → cooking → ready → served. */
const KDS_NEXT: Record<string, "cooking" | "ready" | "served"> = {
  pending: "cooking",
  cooking: "ready",
  ready: "served",
};

export function nextKdsStatus(
  current: string | null | undefined,
): "cooking" | "ready" | "served" | null {
  return KDS_NEXT[current ?? "pending"] ?? null;
}

/**
 * Advances a line to the next kitchen state. No-op if it is already `served`.
 * Returns the new state (or null if it did not advance). Pure transition logic.
 */
export async function advanceLineKdsMutation(
  db: PowerSyncDrizzleDB,
  lineId: string,
  currentStatus: string | null | undefined,
  options?: VerifiedWriteOptions,
): Promise<"cooking" | "ready" | "served" | null> {
  const next = nextKdsStatus(currentStatus);
  if (!next) {
    return null;
  }
  await setLineKdsStatusMutation(db, lineId, next, options);
  return next;
}

export interface SplitPaymentResult {
  paymentId: string;
  change: number;
  /** Balance remaining after this payment (cents). 0 = order settled. */
  remaining: number;
  settled: boolean;
}

/**
 * Records ONE partial payment on a split bill, with an optional tip. If the
 * sum of payments (excluding tips) covers the total, marks the order `paid`.
 *
 * `amount` is the bill amount covered by this payment (cents). `tip` is
 * additional. In cash, `tendered` lets us calculate change from
 * (amount + tip). The split/cash-up is calculated with pure helpers (calc.ts).
 * CC-6 (pure calculation) + CC-7 (writes with await).
 */
export async function splitPaymentMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  amount: number,
  opts?: {
    method?: PaymentMethod;
    tip?: number;
    tendered?: number;
    registerSessionId?: string | null;
    employeeId?: string | null;
  },
  options?: VerifiedWriteOptions,
): Promise<SplitPaymentResult> {
  const current = await db.select().from(order).where(eq(order.id, orderId)).limit(1);
  const total = current[0]?.total ?? 0;
  const method = opts?.method ?? "cash";
  const payAmount = Math.max(0, Math.trunc(amount));
  const tip = Math.max(0, Math.trunc(opts?.tip ?? 0));
  const employeeId = opts?.employeeId ?? current[0]?.employee_id ?? null;

  // Previous payments for this order (to know the remaining balance).
  const prior = await db
    .select()
    .from(payment)
    .where(and(eq(payment.order_id, orderId), ...livePaymentFilters()));
  const priorAmounts = prior.map((p) => p.amount ?? 0);

  // In cash the customer hands over `tendered` for (amount + tip); change
  // comes from that total. Other methods have no change.
  const due = payAmount + tip;
  const tendered = method === "cash" ? Math.trunc(opts?.tendered ?? due) : due;
  const change = method === "cash" ? changeDue(tendered, due) : 0;

  const paymentId = generateId();
  const now = nowISO();
  await db.insert(payment).values({
    id: paymentId,
    organization_id: organizationId,
    order_id: orderId,
    register_session_id: opts?.registerSessionId ?? current[0]?.register_session_id ?? null,
    method,
    amount: payAmount,
    tip,
    tendered,
    change_given: change,
    employee_id: employeeId,
    paid_at: now,
    created_at: now,
    updated_at: now,
  });

  const allAmounts = [...priorAmounts, payAmount];
  const settled = isSplitSettled(total, allAmounts);
  const remaining = remainingDue(total, allAmounts);

  if (settled) {
    if (employeeId && employeeId !== current[0]?.employee_id) {
      await setOrderEmployeeMutation(db, orderId, employeeId, options);
    }
    await setOrderStatusMutation(db, orderId, "paid", options);
  }

  return { paymentId, change, remaining, settled };
}
